// Shared DOM builders for the /finance/* pages: section headings, summary
// tiles, two-column amount tables, wide figure tables and the FY picker.
// Pure row/tile models live in model/finance/*-view.js; these just render them.
import { createCollapsibleFieldset } from '../components/collapsible-fieldset.js';
import { createDetailList } from '../components/detail-list.js';
import { rowAmount } from '../model/finance/income-view.js';
import { formatMoney, moneyHeader } from '../model/finance/money.js';

import './finance.css';

/** An `<h3 class="section-heading">` with optional trailing content (e.g. a tag). */
export function heading(text, extra = null) {
  const h = document.createElement('h3');
  h.className = 'section-heading';
  h.textContent = text;
  if (extra) h.append(' ', extra);
  return h;
}

/**
 * Summary tiles: [{ key, label, cents? | value?, tone?, hint? }] — `cents`
 * shows as whole dollars, else `value` (a string) as-is.
 */
export function renderTiles(tiles) {
  const wrap = document.createElement('div');
  wrap.className = 'finance-tiles';
  for (const t of tiles) {
    const tile = document.createElement('div');
    tile.className = 'finance-tile';
    tile.dataset.key = t.key;
    if (t.tone) tile.classList.add(`is-${t.tone}`);
    const label = document.createElement('span');
    label.className = 'finance-tile__label';
    label.textContent = t.label;
    const value = document.createElement('span');
    value.className = 'finance-tile__value';
    value.textContent =
      t.cents !== undefined && t.cents !== null ? formatMoney(t.cents, { whole: true }) : t.value;
    tile.append(label, value);
    if (t.hint) {
      const h = document.createElement('span');
      h.className = 'finance-tile__hint muted';
      h.textContent = t.hint;
      tile.append(h);
    }
    wrap.append(tile);
  }
  return wrap;
}

/**
 * A two-column label / amount table from [{ label, cents, sign?, strong?,
 * muted? }]. It has no header row, so the caption names the currency
 * ("Tax estimate (AU$)") and the cells use the bare symbol.
 */
export function amountTable(rows, { caption, currency = 'AUD' } = {}) {
  const table = document.createElement('table');
  table.className = 'data-table finance-amounts';
  if (caption) {
    const c = document.createElement('caption');
    c.textContent = moneyHeader(caption, currency);
    table.append(c);
  }
  const body = document.createElement('tbody');
  for (const row of rows) {
    const tr = document.createElement('tr');
    if (row.strong) tr.className = 'is-strong';
    if (row.muted) tr.className = 'muted';
    const th = document.createElement('th');
    th.scope = 'row';
    th.textContent = row.label;
    const td = document.createElement('td');
    td.className = 'is-numeric';
    td.textContent = rowAmount(row, { currency, short: true });
    tr.append(th, td);
    body.append(tr);
  }
  table.append(body);
  return table;
}

function cell(text, tag = 'td') {
  const el = document.createElement(tag);
  el.textContent = text ?? '';
  return el;
}

/**
 * A wide figures table in a sideways scroller: `columns` (header texts, or
 * `{ label, numeric }` — a numeric column's header and cells right-align; text
 * columns left-align), `rows` [{ cells: (string | Node | { text, title })[],
 * className?, title? }],
 * optional `footer` (string[]; the first cell is a row header). Returns the
 * scroller.
 */
export function figuresTable({ columns, rows, footer = null, className = '' }) {
  const table = document.createElement('table');
  table.className = `data-table finance-figures ${className}`.trim();
  const numeric = columns.map((c) => typeof c === 'object' && c !== null && !!c.numeric);
  const align = (el, i) => {
    if (numeric[i]) el.classList.add('is-numeric');
    return el;
  };
  const thead = document.createElement('thead');
  const hr = document.createElement('tr');
  columns.forEach((c, i) => {
    const th = align(cell(typeof c === 'object' && c !== null ? c.label : c, 'th'), i);
    th.scope = 'col';
    hr.append(th);
  });
  thead.append(hr);
  const tbody = document.createElement('tbody');
  for (const r of rows) {
    const tr = document.createElement('tr');
    if (r.className) tr.className = r.className;
    if (r.title) tr.title = r.title;
    tr.append(...r.cells.map((c, i) => align(bodyCell(c), i)));
    tbody.append(tr);
  }
  table.append(thead, tbody);
  if (footer) {
    const tfoot = document.createElement('tfoot');
    const fr = document.createElement('tr');
    fr.append(cell(footer[0], 'th'), ...footer.slice(1).map((c, i) => align(cell(c), i + 1)));
    tfoot.append(fr);
    table.append(tfoot);
  }
  const scroller = document.createElement('div');
  scroller.className = 'table-scroll';
  scroller.append(table);
  return scroller;
}

/** A body `<td>` from a string, a Node, or `{ text, title }` (a tooltip on the cell). */
function bodyCell(c) {
  if (c instanceof Node) {
    const td = document.createElement('td');
    td.append(c);
    return td;
  }
  if (typeof c === 'object' && c !== null) {
    const td = cell(c.text);
    if (c.title) td.title = c.title;
    return td;
  }
  return cell(c);
}

/**
 * A persistent chart figure: `{ figure, canvas, handle: null }` — the page
 * creates its Chart.js handle (lazily imported) on the first draw.
 */
export function chartFigure(ariaLabel) {
  const figure = document.createElement('figure');
  figure.className = 'finance-chart';
  const frame = document.createElement('div');
  frame.className = 'finance-chart__canvas';
  const canvas = document.createElement('canvas');
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', ariaLabel);
  frame.append(canvas);
  figure.append(frame);
  return { figure, canvas, handle: null };
}

/**
 * The add / change button above a list of plan cards (new plans go on top),
 * e.g. "Change regular deposits": `{ label, open }` + an extra class.
 */
export function planButton({ label, open }, className = '') {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = ['secondary small finance-plan-button', className].filter(Boolean).join(' ');
  const icon = document.createElement('i');
  icon.className = 'fa-solid fa-calendar-plus';
  icon.setAttribute('aria-hidden', 'true');
  button.append(icon, ` ${label}`);
  button.addEventListener('click', open);
  return button;
}

/** An `<h4 class="finance-subheading">` dividing a section (e.g. "Sales in 2025–26"). */
export function subheading(text, extra = null) {
  const h = document.createElement('h4');
  h.className = 'finance-subheading';
  h.textContent = text;
  if (extra) h.append(' ', extra);
  return h;
}

/**
 * A card's block content: its detail list + a collapsible table (e.g. a sale's
 * "Vests sold"). `legend` is static text (set as HTML); `expanded` /
 * `onToggle` let the page remember the open state across re-renders.
 */
export function cardTableContent(details, { legend, table, className, expanded, onToggle }) {
  const { fieldset } = createCollapsibleFieldset({
    legendHtml: legend,
    content: table,
    expanded,
    onToggle,
  });
  fieldset.classList.add('panel-fieldset', 'finance-card-table', className);
  const frag = document.createDocumentFragment();
  frag.append(createDetailList(details), fieldset);
  return frag;
}

/** Fill an FY `<select>` from [{ value, label }] and select `value`. */
export function fillFySelect(select, options, value) {
  select.replaceChildren(
    ...options.map((o) => {
      const opt = document.createElement('option');
      opt.value = String(o.value);
      opt.textContent = `FY ${o.label}`;
      return opt;
    }),
  );
  select.value = String(value);
}
